import { Organization } from '../../interfaces/api/organization';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { OrganizationFilterParameters } from '../../api/organizations';
import { fetchOrganizations } from '../services/organization.service';

interface OrganizationState {
	organizations: {
		byId: Record<number, Organization>;
		all: Organization[];
	};
	selectedOrganization: Organization | undefined;
	isLoading: boolean;
}

const initialState: OrganizationState = {
	organizations: {
		byId: {},
		all: [],
	},
	selectedOrganization: undefined,
	isLoading: false,
};

export const fetchOrganizationsThunk = createAsyncThunk<
	Organization[],
	{ params: OrganizationFilterParameters }
>('organization/fetchOrganizations', async ({ params }, { rejectWithValue }) => {
	try {
		return await fetchOrganizations(params);
	} catch (error) {
		return rejectWithValue(error);
	}
});

const organizationSlice = createSlice({
	name: 'organization',
	initialState,
	reducers: {
		selectOrganization: (state, action: PayloadAction<number>) => {
			state.selectedOrganization = state.organizations.byId[action.payload];
			localStorage.setItem(
				'selectedOrganization',
				String(state.organizations.byId[action.payload].id)
			);
		},
		unSelectOrganization: (state) => {
			state.selectedOrganization = undefined;
			localStorage.removeItem('selectedOrganization');
		},
		resetOrganization: (state) => {
			Object.assign(state, initialState);
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchOrganizationsThunk.fulfilled, (state, action) => {
			const byId: Record<number, Organization> = {};
			action.payload.forEach((org) => {
				byId[org.id] = org;
			});
			const all = action.payload.map((org) => org);
			state.organizations = { byId, all };
			const selectedStorageId = localStorage.getItem('selectedOrganization');
			if (selectedStorageId && byId[Number(selectedStorageId)]) {
				state.selectedOrganization = byId[Number(selectedStorageId)];
			}
			state.isLoading = false;
		});
		builder.addCase(fetchOrganizationsThunk.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(fetchOrganizationsThunk.rejected, (state, _action) => {
			state.organizations = {
				byId: {},
				all: [],
			};
			state.isLoading = false;
		});
	},
});

export const { selectOrganization, unSelectOrganization, resetOrganization } =
	organizationSlice.actions;

export default organizationSlice;
